import { computed, inject, Injector, signal } from '@angular/core';
import { CommonDateTimeField, CommonDateTimeFieldConfig, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField, UtilityService } from '@eforall/common';
import {
	EngagementActivity,
	EngagementExternalRole,
	EngagementExternalRoleId,
	EngagementForm,
} from '../../../../interfaces';
import { DomainDataService } from '../../../../services';

export class EngagementEditorForm {

	private readonly util = inject(UtilityService);
	public isDonorPotential = computed(() => this.values().isDonorPotential);
	private readonly injector = inject(Injector)
	private readonly domainData = inject(DomainDataService);

	public readonly commonForm = new CommonForm(this.injector);

	public values = signal<EngagementForm>({
		personId: 0,
		engagementDateUTC: this.util.date.toUTC(new Date())!,
		staffRoleId: undefined,
		externalRoleId: undefined,
		purposeId: 0,
		activity: EngagementActivity.AwaitingResponse,
		details: '',
		isDonorPotential: false,
		donorPotentialDetails: '',
		contactInteraction: this.defaultContactInteractionValues()
	});

	readonly engagementDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({ label: 'Engagement Date', isRequired: true, isSpanish: false, isEnabled: signal(true), type: 'event', includeTime: false }),
		actualValue: computed(() => this.values().engagementDateUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		individualSave: async (value: number | undefined) => {
			const values = this.values();
			this.values.set({ ...values, engagementDateUTC: value || 0 });
		},
		error: signal(''),
		formFieldName: 'engagement-date',
		form: this.commonForm,
	}));

	private readonly externalRolesOptions = computed(() => [
		{ value: EngagementExternalRoleId.Entrepreneur, text: EngagementExternalRole.Entrepreneur },
		{ value: EngagementExternalRoleId.Volunteer, text: EngagementExternalRole.Volunteer },
		{ value: EngagementExternalRoleId.Partner, text: EngagementExternalRole.Partner },
	]);

	readonly externalRoleId = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'External Role', options: this.externalRolesOptions(), isRequired: true, isSpanish: false, isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().externalRoleId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, externalRoleId: value });
		},
		error: signal(''),
		formFieldName: 'external-role',
		form: this.commonForm,
	}));

	private readonly staffRolesOptions = computed(() => this.domainData.data().staffRoles.map(type => ({ value: type.staffRoleId, text: type.name })));

	readonly staffRoleId = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Staff Role', options: this.staffRolesOptions(), isRequired: true, isSpanish: false,
			isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().staffRoleId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, staffRoleId: value });
		},
		error: signal(''),
		formFieldName: 'staff-role',
		form: this.commonForm,
	}));


	private readonly purposeOptions = computed(() => {
		const domainData = this.domainData.data();
		return domainData.engagementPurposes.map(purpose =>
			({ text: purpose.name, value: purpose.purposeId })
		).sort((a, b) => a.text < b.text ? -1 : 1);
	});

	readonly purpose = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Purpose', options: this.purposeOptions(), isRequired: true, isSpanish: false, isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().purposeId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, purposeId: value });
		},
		error: signal(''),
		formFieldName: 'purpose',
		form: this.commonForm,
	}));

	private readonly activityOptions = computed(() => [
		{ value: EngagementActivity.AwaitingResponse, text: EngagementActivity.AwaitingResponse },
		{ value: EngagementActivity.Engaged, text: EngagementActivity.Engaged },
	]);

	readonly activity = computed<CommonSelectField<string>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Activity', options: this.activityOptions(),
			isRequired: true, isSpanish: false, isEnabled: signal(true)
		}),
		actualValue: computed(() => this.values().activity),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			const values = this.values();
			this.values.set({ ...values, activity: value as EngagementActivity });
		},
		error: signal(''),
		formFieldName: 'activity',
		form: this.commonForm,
	}));

	private readonly contactMethodOptions = computed(() => {
		const domainData = this.domainData.data();
		return domainData.contactMethods.map(contactMethod =>
			({ value: contactMethod.contactMethodId, text: contactMethod.name })
		).sort((a, b) => a.text < b.text ? -1 : 1);
	});

	readonly contactMethod = computed<CommonSelectField<number>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Contact Method', options: this.contactMethodOptions(), isRequired: true, isSpanish: false,
			isEnabled: signal(true),
		}),
		actualValue: computed(() => this.values().contactInteraction.contactMethodId || 0),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		individualSave: async (value: number) => {
			const values = this.values();
			this.values.set({ ...values, contactInteraction: { ...values.contactInteraction, contactMethodId: value } });
		},
		error: signal(''),
		formFieldName: 'contact-method',
		form: this.commonForm,
	}));

	readonly contactedDate = computed<CommonDateTimeField>(() => ({
		config: signal<CommonDateTimeFieldConfig>({
			label: 'Contacted Date', isRequired: true, isSpanish: false, type: 'event', includeTime: false,
			isEnabled: signal(true),
		}),
		actualValue: computed(() => this.values().contactInteraction.contactedDateUTC),
		pendingValue: signal<number | undefined | null>(null),
		saving: signal(false),
		individualSave: async (value: number | undefined) => {
			const values = this.values();
			this.values.set({ ...values, contactInteraction: { ...values.contactInteraction, contactedDateUTC: value! } });
		},
		error: signal(''),
		formFieldName: 'contacted-date',
		form: this.commonForm,
	}));

	readonly details = computed<CommonTextField>(() => ({
		config: signal({ label: 'Details', min: 1, max: 2000, fixCase: 'sentence-case', isRequired: true, multiLine: true, isSpanish: false, isEnabled: signal(true) }),
		actualValue: computed(() => this.values().details || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			const values = this.values();
			this.values.set({ ...values, details: value });
		},
		error: signal(''),
		formFieldName: 'details',
		form: this.commonForm,
	}));

	readonly donorPotentialDetails = computed<CommonTextField>(() => ({
		config: signal({ label: 'Donor Potential', isRequired: true, min: this.isDonorPotential() ? 1 : 0, max: 2000, fixCase: 'sentence-case', multiLine: true, isSpanish: false, isEnabled: signal(true), }),
		actualValue: computed(() => this.values().donorPotentialDetails || ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		individualSave: async (value: string) => {
			const values = this.values();
			this.values.set({ ...values, donorPotentialDetails: value });
		},
		error: signal(''),
		formFieldName: 'donor-potential-details',
		form: this.commonForm,
	}));

	checkIfValid() {
		const { contactInteraction, engagementDateUTC, externalRoleId, purposeId, activity, personId, staffRoleId, donorPotentialDetails } = this.values();

		if (!contactInteraction.contactMethodId || !contactInteraction.staffId || !contactInteraction.contactedDateUTC)
			return false;

		const fields = [
			this.contactMethod().error(), this.contactedDate().error(), this.engagementDate().error(),
			this.externalRoleId().error(), this.purpose().error(), this.staffRoleId().error(), this.activity().error(),
			this.donorPotentialDetails().error()
		];

		const requiredValues = [engagementDateUTC, externalRoleId, purposeId, activity, personId, staffRoleId];

		if (fields.some(Boolean) || requiredValues.some(v => !v))
			return false;
		else if (this.isDonorPotential() && !donorPotentialDetails)
			return false;

		return true;
	}


	public resetForm() {
		this.values.set({
			personId: 0,
			engagementDateUTC: this.util.date.toUTC(new Date())!,
			staffRoleId: undefined,
			externalRoleId: undefined,
			purposeId: 0,
			activity: EngagementActivity.AwaitingResponse,
			details: '',
			isDonorPotential: false,
			donorPotentialDetails: '',
			contactInteraction: this.defaultContactInteractionValues()
		});
		this.engagementDate().pendingValue.set(null);
		this.externalRoleId().pendingValue.set(null);
		this.purpose().pendingValue.set(null);
		this.activity().pendingValue.set(null);
		this.contactMethod().pendingValue.set(null);
		this.contactedDate().pendingValue.set(null);
		this.details().pendingValue.set(null);
		this.donorPotentialDetails().pendingValue.set(null);
		this.commonForm.resetAll();
	}

	public defaultContactInteractionValues() {
		return {
			contactMethodId: 0,
			contactedDateUTC: this.util.date.toUTC(new Date())!,
			note: '',
			staffId: undefined
		}
	}
}